/** React shell around the imperative Cytoscape view (I12): mounts once, re-renders on data changes. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Crosshair, Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import { clsx } from "clsx";
import { getMeta, setMeta } from "../sync/persistence";
import type { MappedGraph } from "./conventionMapper";
import { mountCytoscape, type CyHandle, type LayoutName, type Positions } from "./CytoscapeView";
import type { NodeInfo } from "./nodeInfo";
import type { StyleOverrides } from "./stylesheet";
import { useT } from "../i18n";

/** one step of the `+` / `−` buttons (spec §15.7) */
const ZOOM_STEP = 1.3;
/** the auto-centre toggle: a per-reader preference in IndexedDB, never in Loro (I1, §15.7) */
const AUTOCENTRE_KEY = "graph-autocenter";

export interface GraphCanvasProps {
  graph: MappedGraph | null;
  info: Map<string, NodeInfo>;
  layout: LayoutName;
  positions?: Positions;
  parents?: Map<string, string>;
  overrides?: StyleOverrides;
  incremental?: boolean;
  onSelect?(id: string | null): void;
  onExpand?(id: string): void;
  onDragEnd?(id: string, pos: { x: number; y: number }): void;
  className?: string;
}

export function GraphCanvas({ graph, info, layout, positions, parents, overrides, incremental, onSelect, onExpand, onDragEnd, className }: GraphCanvasProps) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const handle = useRef<CyHandle | null>(null);
  const callbacks = useRef({ onSelect, onExpand, onDragEnd });
  const [ready, setReady] = useState(false);
  // [M21] the zoom overlay's disabled state follows the renderer's own bounds (spec §15.7)
  const [zoom, setZoom] = useState<{ level: number; min: number; max: number } | null>(null);
  // [M21] whether a re-run pans its results into view at the reader's zoom (§15.8); on by default
  const [autoCentre, setAutoCentre] = useState(true);
  const autoCentreRef = useRef(true);

  useEffect(() => {
    let alive = true;
    void getMeta<boolean>(AUTOCENTRE_KEY).then((v) => {
      if (alive && typeof v === "boolean") {
        setAutoCentre(v);
        autoCentreRef.current = v;
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    callbacks.current = { onSelect, onExpand, onDragEnd };
  }, [onSelect, onExpand, onDragEnd]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let alive = true;
    void mountCytoscape(el, {
      onSelect: (id) => callbacks.current.onSelect?.(id),
      onExpand: (id) => callbacks.current.onExpand?.(id),
      onDragEnd: (id, pos) => callbacks.current.onDragEnd?.(id, pos),
    }).then((h) => {
      if (!alive) {
        h.destroy();
        return;
      }
      handle.current = h;
      setReady(true);
    });
    return () => {
      alive = false;
      handle.current?.destroy();
      handle.current = null;
    };
  }, []);

  useEffect(() => {
    if (!ready || !handle.current || !graph) return;
    // autoCentre is read from a ref: changing the toggle must not re-render the graph
    handle.current.render(graph, info, { layout, positions, parents, overrides, incremental, autoCentre: autoCentreRef.current });
  }, [ready, graph, info, layout, positions, parents, overrides, incremental]);

  useEffect(() => {
    const h = handle.current;
    if (!ready || !h) return;
    const read = () => setZoom({ level: h.cy.zoom(), min: h.cy.minZoom(), max: h.cy.maxZoom() });
    read();
    h.cy.on("zoom", read);
    return () => {
      h.cy.off("zoom", read);
    };
  }, [ready]);

  // the CSS rule of §8.21 cannot reach a JS-driven animation, so ask the media query directly
  const animate = useCallback(() => !window.matchMedia("(prefers-reduced-motion: reduce)").matches, []);

  return (
    <div className={clsx("relative", className ?? "h-full w-full")}>
      <div ref={ref} className="h-full w-full" data-testid="graph-canvas" />
      {ready && (
        <div
          className="absolute right-1 top-1 z-10 flex flex-col overflow-hidden rounded border border-neutral-300 bg-[var(--surface)] text-neutral-600 shadow-[var(--shadow-sm)] dark:border-neutral-700 dark:text-neutral-300"
          role="group"
          aria-label={t("graph.zoom")}
          data-testid="graph-zoom"
        >
          <ZoomButton label={t("graph.zoomIn")} testId="graph-zoom-in" disabled={!!zoom && zoom.level >= zoom.max} onClick={() => handle.current?.zoomBy(ZOOM_STEP, animate())}>
            <ZoomIn size={14} />
          </ZoomButton>
          <ZoomButton label={t("graph.zoomOut")} testId="graph-zoom-out" disabled={!!zoom && zoom.level <= zoom.min} onClick={() => handle.current?.zoomBy(1 / ZOOM_STEP, animate())}>
            <ZoomOut size={14} />
          </ZoomButton>
          <ZoomButton label={t("graph.fit")} testId="graph-zoom-fit" onClick={() => handle.current?.fitView(animate())}>
            <Maximize2 size={14} />
          </ZoomButton>
          <ZoomButton
            label={t("graph.autoCentre")}
            testId="graph-autocenter"
            pressed={autoCentre}
            onClick={() => {
              const next = !autoCentre;
              setAutoCentre(next);
              autoCentreRef.current = next;
              void setMeta(AUTOCENTRE_KEY, next);
            }}
          >
            <Crosshair size={14} />
          </ZoomButton>
        </div>
      )}
    </div>
  );
}

function ZoomButton({ label, testId, disabled, pressed, onClick, children }: { label: string; testId: string; disabled?: boolean; pressed?: boolean; onClick(): void; children: React.ReactNode }) {
  return (
    <button
      className="border-t border-neutral-200 px-1.5 py-1 transition first:border-t-0 hover:bg-neutral-100 disabled:opacity-30 disabled:hover:bg-transparent aria-pressed:bg-neutral-200 aria-pressed:text-neutral-900 dark:border-neutral-700 dark:hover:bg-neutral-800 dark:aria-pressed:bg-neutral-700 dark:aria-pressed:text-neutral-100"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      data-testid={testId}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
